import React, { useEffect, useMemo, useState } from "react";

import type { OptionType, PickMainState } from "react-lib/apps/HISV3/common/CommonInterface";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

import { AlertV4, ButtonV4, FieldV4, ModalV4, SelectV4, TabsV4 } from "../common/ui";

import { stageOf } from "../common/commonV4Interface";
import { opdOADivisionOptions } from "../common/oaProvidersV4";
import { useOAProvidersV4 } from "../common/useOAProvidersV4";
import {
  formatAppointmentRows,
  type AppointmentItem,
  type EncounterItem,
} from "./registrationModelV4";

import "./_hisv4-registration.scss";

type EncounterTab = "appointment" | "walkin";

type CreateUpdateEncounterSlice = Partial<{
  division: number | string | null;
  doctor: number | string | null;
  encounterList: EncounterItem[];
  selectedEncounterItems: number[];
}> | null;

export type CardOpenEncounterV4Props = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "successMessage"
> & {
  setProp: (key: string, value: unknown) => void;
  runSequence: (params: Record<string, unknown>) => void;
  CreateUpdateEncounterSequence?: CreateUpdateEncounterSlice;
  appointmentEncounterList?: AppointmentItem[];
  masterOptions?: Partial<Record<"divisionOpd" | "doctor", OptionType[]>>;
  rehabAppointmentDoctors?: unknown[] | null;
  selectedAppointmentItems?: number[];
  userName?: string;
  apiToken?: string;
  divisionOptions?: OptionType[];
  selectedDivisionId?: number;
  onOpened?: () => void;
};

const ENCOUNTER_SEQUENCE = "CreateUpdateEncounter";

// card เดียวกับ CardOpenEncounter: sequence เขียน error/success/buttonLoadCheck ด้วย key นี้
const ENCOUNTER_CARD = "CreateUpdateEncounter";

const OPEN_APPOINTMENT = "open_encounter_appointment";

const PRINT_SLIP = "print_visit_slip_form";

const SAVE_KEY = `${ENCOUNTER_CARD}_SAVE`;

const OPEN_APPOINTMENT_KEY = `${ENCOUNTER_CARD}_${OPEN_APPOINTMENT}`;

const PRINT_SLIP_KEY = `${ENCOUNTER_CARD}_${PRINT_SLIP}`;

const textOfOption = (options: OptionType[], value: unknown): string =>
  options.find((option) => String(option.value) === String(value ?? ""))?.text ?? "";

/** เปิด Encounter ของผู้ป่วย: มาตามนัด / Walk-in + Encounter ของผู้ป่วย */
const CardOpenEncounterV4 = (props: CardOpenEncounterV4Props) => {
  const [tab, setTab] = useState<EncounterTab>("walkin");
  const [opened, setOpened] = useState(false);

  const seq = props.CreateUpdateEncounterSequence;
  const divisions = opdOADivisionOptions(props.divisionOptions, props.masterOptions?.divisionOpd);
  const validDivision = divisions.some((option) => Number(option.value ?? option.id) === Number(seq?.division));
  const providers = useOAProvidersV4(props.apiToken, validDivision ? Number(seq?.division) : undefined);
  const doctors = providers.doctors.map((doctor) => ({ value: doctor.id, text: doctor.name_code || doctor.full_name || "" }));
  const validDoctor = doctors.some((doctor) => doctor.value === Number(seq?.doctor));

  useEffect(() => {
    if (!seq || validDivision || divisions.length === 0) return;
    const initial = divisions.find((option) => Number(option.value ?? option.id) === props.selectedDivisionId);
    props.setProp("CreateUpdateEncounterSequence.division", initial ? Number(initial.value ?? initial.id) : "");
    props.setProp("CreateUpdateEncounterSequence.doctor", "");
  }, [!!seq, seq?.division, validDivision, props.selectedDivisionId, props.divisionOptions, props.masterOptions?.divisionOpd]);

  useEffect(() => {
    if (seq?.doctor && !providers.loading && !validDoctor) {
      props.setProp("CreateUpdateEncounterSequence.doctor", "");
    }
  }, [seq?.doctor, providers.loading, validDoctor]);

  const selectedAppointments = props.selectedAppointmentItems ?? [];

  const rawError = props.errorMessage?.[ENCOUNTER_CARD];

  // error ที่มีรายการ detail = ต้องยืนยันก่อนเปิดจากนัด
  const confirmError =
    Array.isArray(rawError?.detail) && typeof rawError?.error === "string" ? rawError : null;

  const error = confirmError ? null : rawError;

  const appointments = useMemo(
    () => formatAppointmentRows(props.appointmentEncounterList ?? []),
    [props.appointmentEncounterList],
  );

  const encounters = useMemo(
    () => (seq?.encounterList ?? []).filter((item) => item.status !== "CANCELED"),
    [seq?.encounterList],
  );

  const sendTo = useMemo(() => {
    if (tab === "appointment") {
      const firstAppointment = appointments.find((row) => selectedAppointments.includes(row.id));

      return [
        firstAppointment?.division || "ยังไม่เลือกนัด",
        firstAppointment?.doctor || "ยังไม่เลือกแพทย์",
      ];
    }

    return [
      textOfOption(divisions, seq?.division) || "ยังไม่เลือกแผนก",
      textOfOption(doctors as OptionType[], seq?.doctor) || "ยังไม่เลือกแพทย์",
    ];
  }, [tab, appointments, selectedAppointments, props.divisionOptions, providers.doctors, seq?.division, seq?.doctor]);

  useEffect(() => {
    props.runSequence({
      sequence: ENCOUNTER_SEQUENCE,
      card: ENCOUNTER_CARD,
      restart: true,
      isNoneFilterDate: true,
    });

    return () => {
      props.setProp(`successMessage.${ENCOUNTER_CARD}`, null);
      props.setProp(`errorMessage.${ENCOUNTER_CARD}`, null);
      props.setProp("CreateUpdateEncounterSequence.selectedEncounterItems", []);
    };
  }, []);

  // มีนัดของผู้ป่วยแล้ว เปิดที่แท็บนัดก่อน
  useEffect(() => {
    if (appointments.length > 0) {
      setTab("appointment");
    }
  }, [appointments.length > 0]);

  useEffect(() => {
    if (!props.successMessage?.[ENCOUNTER_CARD]) {
      return;
    }

    props.setProp(`successMessage.${ENCOUNTER_CARD}`, null);

    setOpened(true);

    props.onOpened?.();
  }, [props.successMessage?.[ENCOUNTER_CARD]]);

  const handleToggleAppointment = (id: number) => {
    props.setProp(
      "selectedAppointmentItems",
      selectedAppointments.includes(id)
        ? selectedAppointments.filter((item) => item !== id)
        : [...selectedAppointments, id],
    );
  };

  const handleOpenAppointment = (confirmed?: boolean) => {
    props.runSequence({
      sequence: ENCOUNTER_SEQUENCE,
      action: OPEN_APPOINTMENT,
      card: ENCOUNTER_CARD,
      confirmed,
    });
  };

  const handleCreate = () => {
    if (!validDivision || !validDoctor || providers.loading || providers.error) return;
    setOpened(false);

    props.runSequence({
      sequence: ENCOUNTER_SEQUENCE,
      action: "create",
      card: ENCOUNTER_CARD,
      buttonLoadKey: SAVE_KEY,
    });
  };

  const handlePrint = () => {
    props.runSequence({ sequence: ENCOUNTER_SEQUENCE, action: PRINT_SLIP, card: ENCOUNTER_CARD });
  };

  const handleCloseError = () => {
    props.setProp(`errorMessage.${ENCOUNTER_CARD}`, null);
  };

  return (
    <div className="hv4-reg-oe">
      <div className="hv4-page-head">
        <h1 className="hv4-h1">เปิด Encounter</h1>
        <span className="hv4-page-sub">เลือกว่าผู้ป่วยมาตามนัด หรือมาโดยไม่มีนัด (Walk-in)</span>
      </div>

      <TabsV4<EncounterTab>
        ariaLabel="ประเภทการมา"
        items={[
          { key: "appointment", label: `มีนัดหมาย (${appointments.length})` },
          { key: "walkin", label: "ไม่มีนัด · Walk-in" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {opened && (
        <AlertV4 tone="info" onClose={() => setOpened(false)}>
          เปิด Encounter แล้ว
        </AlertV4>
      )}

      <AlertV4 tone="danger" error={error} onClose={handleCloseError} />

      {props.rehabAppointmentDoctors?.length ? (
        <AlertV4 tone="warn" onClose={() => props.setProp("rehabAppointmentDoctors", null)}>
          นัดกายภาพบำบัดที่เลือกยังไม่ระบุแพทย์ จึงเปิด Encounter ไม่ได้
        </AlertV4>
      ) : null}

      {tab === "appointment" ? (
        <section className="hv4-card">
          <h2 className="hv4-card-title">นัดหมายของผู้ป่วย</h2>
          <p className="hv4-reg-oe-sub">
            เลือกนัดที่ผู้ป่วยมาตามนัดวันนี้ ระบบดึงแพทย์ แผนก และรายละเอียดการนัดให้เอง
          </p>
          <div className="hv4-reg-oe-table">
            <table className="hisv4-table">
              <thead>
                <tr>
                  <th aria-label="เลือก" />
                  <th>วันที่นัด</th>
                  <th>เวลานัด</th>
                  <th>ชื่อแพทย์</th>
                  <th>แผนก</th>
                  <th>Orders</th>
                  <th>รายละเอียดการนัด</th>
                </tr>
              </thead>
              <tbody>
                {appointments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="hisv4-table-empty">
                      ไม่มีนัดหมายที่เปิด Encounter ได้
                    </td>
                  </tr>
                ) : (
                  appointments.map((row) => (
                    <tr
                      key={row.id}
                      className={selectedAppointments.includes(row.id) ? "hv4-reg-oe-row--on" : ""}
                      onClick={() => handleToggleAppointment(row.id)}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`เลือกนัด ${row.date} ${row.time}`}
                          checked={selectedAppointments.includes(row.id)}
                          onChange={() => handleToggleAppointment(row.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{row.date || "-"}</td>
                      <td>{row.time || "-"}</td>
                      <td>{row.doctor || "-"}</td>
                      <td>{row.division || "-"}</td>
                      <td>{row.orders || "-"}</td>
                      <td>{row.detail || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="hv4-card">
          <h2 className="hv4-card-title">เปิด Encounter ใหม่ (ไม่มีนัด)</h2>
          <p className="hv4-reg-oe-sub">เลือกแผนกและแพทย์ที่จะตรวจ แล้วเปิดได้เลย</p>
          {providers.error && <AlertV4 tone="danger">{providers.error} <button onClick={providers.reload}>ลองใหม่</button></AlertV4>}
          <div className="hv4-reg-oe-form">
            <FieldV4 label="แผนก *" htmlFor="hv4-reg-oe-division">
              <SelectV4
                id="hv4-reg-oe-division"
                search
                placeholder="เลือกแผนก"
                options={divisions}
                value={String(seq?.division ?? "")}
                onChange={(value) => {
                  props.setProp("CreateUpdateEncounterSequence.doctor", "");
                  props.setProp(
                    "CreateUpdateEncounterSequence.division",
                    value === "" ? "" : Number(value),
                  );
                }}
              />
            </FieldV4>
            <FieldV4 label="แพทย์ผู้ตรวจ *" htmlFor="hv4-reg-oe-doctor">
              <SelectV4
                id="hv4-reg-oe-doctor"
                search
                placeholder="เลือกแพทย์"
                options={doctors}
                disabled={!validDivision || providers.loading || !!providers.error}
                value={validDoctor ? String(seq?.doctor) : ""}
                onChange={(value) =>
                  props.setProp(
                    "CreateUpdateEncounterSequence.doctor",
                    value === "" ? "" : Number(value),
                  )
                }
              />
            </FieldV4>
          </div>
        </section>
      )}

      {/* สรุป + ปุ่มเปิด */}
      <div className="hv4-reg-bar">
        <span className="hv4-reg-bar-text">
          ส่งไป: {sendTo.join(" · ")}
          {props.userName ? ` · บันทึกโดย ${props.userName}` : ""}
        </span>
        {tab === "appointment" ? (
          <ButtonV4
            className="hv4-reg-btn-encounter"
            disabled={
              selectedAppointments.length === 0 ||
              props.buttonLoadCheck?.[OPEN_APPOINTMENT_KEY] === "LOADING"
            }
            onClick={() => handleOpenAppointment()}
          >
            ลงทะเบียนจากนัดหมาย (เปิด Encounter)
          </ButtonV4>
        ) : (
          <ButtonV4
            className="hv4-reg-btn-encounter"
            disabled={
              !validDivision || !validDoctor || providers.loading || !!providers.error || props.buttonLoadCheck?.[SAVE_KEY] === "LOADING"
            }
            onClick={handleCreate}
          >
            เปิด Encounter
          </ButtonV4>
        )}
      </div>

      {/* Encounter ของผู้ป่วย */}
      <section className="hv4-card">
        <div className="hv4-reg-oe-enc-head">
          <h2 className="hv4-card-title">Encounter ของผู้ป่วย</h2>
          <ButtonV4
            variant="ghost"
            disabled={
              !seq?.selectedEncounterItems?.length ||
              props.buttonLoadCheck?.[PRINT_SLIP_KEY] === "LOADING"
            }
            onClick={handlePrint}
          >
            พิมพ์บัตรเข้ารับการรักษา
          </ButtonV4>
        </div>
        <div className="hv4-reg-oe-table">
          <table className="hisv4-table">
            <thead>
              <tr>
                <th>Encounter</th>
                <th>แผนก</th>
                <th>แพทย์</th>
                <th>สถานะ</th>
                <th>เริ่มเมื่อ</th>
              </tr>
            </thead>
            <tbody>
              {encounters.length === 0 ? (
                <tr>
                  <td colSpan={5} className="hisv4-table-empty">
                    ยังไม่มี Encounter
                  </td>
                </tr>
              ) : (
                encounters.map((row) => {
                  const on = !!seq?.selectedEncounterItems?.includes(row.id);
                  return (
                    <tr
                      key={row.id}
                      aria-selected={on}
                      className={on ? "hv4-reg-oe-row--on" : ""}
                      onClick={() =>
                        props.setProp("CreateUpdateEncounterSequence.selectedEncounterItems", [
                          row.id,
                        ])
                      }
                    >
                      <td className="hv4-reg-oe-no">{row.number || "-"}</td>
                      <td>{row.division_name || "-"}</td>
                      <td>{row.doctor_name || "-"}</td>
                      <td>
                        <span
                          className={`hv4-enc-status ${stageOf({ status: row.status })?.key ?? ""}`}
                        >
                          {row.status_name || "-"}
                        </span>
                      </td>
                      <td>
                        {formatISOtoBEDateTime(row.started || row.created).dateTimeShort || "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {confirmError && (
        <ModalV4 title="ยืนยันเปิด Encounter" width={480} onClose={handleCloseError}>
          <div className="hv4-reg-modal-body">
            <strong>{confirmError.error}</strong>
            <ul>
              {confirmError.detail.map((item: { name?: string }, index: number) => (
                <li key={index}>{item.name}</li>
              ))}
            </ul>
          </div>
          <div className="hv4-reg-modal-foot">
            <ButtonV4 variant="ghost" onClick={handleCloseError}>
              ยกเลิก
            </ButtonV4>
            <ButtonV4
              variant="ok"
              onClick={() => {
                handleCloseError();
                handleOpenAppointment(true);
              }}
            >
              ยืนยัน
            </ButtonV4>
          </div>
        </ModalV4>
      )}
    </div>
  );
};

export default CardOpenEncounterV4;
